/**
 * =============================================================================
 * IDS CRM Web - Engineer bulletins: browse
 * =============================================================================
 *
 * Gareth 2026-09-11: "a bulletin area where we can share info with the
 * engineers ... if we see a repeated fault and we work out how to fix it".
 *
 * Everyone can browse published and replaced bulletins, and anyone can write
 * one. The review queue is only a tab for team leaders, managers and admins,
 * who publish (Gareth 2026-09-13: "team leads - yes").
 *
 * Cards, not a table: the shared App.css list-table rule is fixed-layout at
 * 100% width, which crushes columns to nothing on a phone in a parlour.
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseFaultCodes } from '../utils/faultCodes';
import { formatDate } from '../utils/format';
import { isBulletinReviewer } from '../utils/bulletins';
import { BulletinStatusBadge, FaultCodeChips } from '../components/BulletinParts';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Bulletins.css';

export const PAGE_SIZE = 25;
export const FILTER_DEBOUNCE_MS = 300;

export const BULLETIN_TABS = [
  {
    key: 'published',
    label: 'Published',
    params: { status: 'published' },
    empty: 'No bulletins have been published yet. When a fix for a repeated fault is worked out, write it up so the next engineer has it.'
  },
  {
    key: 'review',
    label: 'Waiting for review',
    params: { status: 'draft', submitted: 'true' },
    reviewersOnly: true,
    empty: 'Nothing is waiting for review.'
  },
  {
    key: 'mine',
    label: 'My drafts',
    params: { status: 'draft', mine: 'true' },
    empty: 'You have no drafts. Use Write a bulletin to start one.'
  },
  {
    key: 'replaced',
    label: 'Replaced',
    params: { status: 'superseded' },
    empty: 'No bulletins have been replaced yet.'
  }
];

function cardDateLabel(bulletin) {
  if (bulletin.status !== 'draft' && bulletin.published_at) return `Published ${formatDate(bulletin.published_at)}`;
  if (bulletin.submitted_at) return `Sent for review ${formatDate(bulletin.submitted_at)}`;
  return `Updated ${formatDate(bulletin.updated_at || bulletin.created_at)}`;
}

// The list gives the first 280 characters of the text; say when there is more.
const EXCERPT_LENGTH = 280;

function BulletinCard({ bulletin }) {
  const groupNames = Array.isArray(bulletin.group_names) ? bulletin.group_names : [];
  const attachments = Number(bulletin.attachment_count) || 0;
  const excerpt = bulletin.excerpt || '';
  return (
    <Link to={`/bulletins/${bulletin.bulletin_id}`} className="bulletin-card">
      <span className="bulletin-card-top">
        <span className="bulletin-card-title">{bulletin.title}</span>
        <BulletinStatusBadge bulletin={bulletin} />
      </span>
      {excerpt && (
        <span className="bulletin-card-excerpt">
          {excerpt}{excerpt.length >= EXCERPT_LENGTH ? '…' : ''}
        </span>
      )}
      <FaultCodeChips codes={bulletin.fault_codes} />
      <span className="bulletin-card-meta">
        <span>{bulletin.created_by_name || 'Unknown author'}</span>
        <span>{cardDateLabel(bulletin)}</span>
        {attachments > 0 && <span>{attachments} file{attachments === 1 ? '' : 's'}</span>}
        {groupNames.length > 0 && <span>Shared with {groupNames.join(', ')}</span>}
      </span>
    </Link>
  );
}

function Bulletins() {
  const { authFetch, user } = useAuth();
  const reviewer = isBulletinReviewer(user);
  const [searchParams, setSearchParams] = useSearchParams();
  // Coming back from a bulletin (a "←" link or the browser's Back) shows the
  // list as it was left; a fresh visit starts from the address.
  const memory = useListMemory('/bulletins');

  const tabs = BULLETIN_TABS.filter(tab => !tab.reviewersOnly || reviewer);
  // The tab lives in the URL so Back from a bulletin returns to the same list.
  // A non-reviewer following a ?tab=review link lands on Published instead.
  const activeTab = tabs.find(tab => tab.key === searchParams.get('tab')) || tabs[0];
  const tabKey = activeTab.key;

  const [searchInput, setSearchInput] = useState(() => memory.initial('search', ''));
  const [codesInput, setCodesInput] = useState(() => memory.initial('codes', ''));

  // Keyed on the normalised list, so "E101" becoming "e101, " is not a new
  // search: only a change to the codes that would be sent fetches again.
  const codesKey = parseFaultCodes(codesInput).join(',');

  // What is sent. Both start from what is in the boxes, so a list being
  // returned to asks once, for the search it was left on.
  const [query, setQuery] = useState(() => searchInput.trim());
  const [codes, setCodes] = useState(codesKey);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setCodes(codesKey), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [codesKey]);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  // Which tab and filters the rows on screen belong to. Until the answer for
  // the current ones arrives the list says Loading, rather than showing the
  // last tab's cards or a "no match" that is not true yet.
  const [loadedKey, setLoadedKey] = useState(null);
  const filterKey = `${tabKey}|${query}|${codes}`;
  // Only the newest request may fill the list. On a slow farm connection an
  // older search can land after a newer one, as SearchSelect guards against.
  const latestRequest = useRef(0);

  // How many pages Load more has brought in. Another tab or search starts
  // again from one page; a list being returned to starts from where it was.
  const [pages, setPages] = useState(() => memory.initial('pages', 1));
  const [pagesKey, setPagesKey] = useState(filterKey);
  if (pagesKey !== filterKey) {
    setPagesKey(filterKey);
    setPages(1);
  }
  // Read when a tab or search first loads, never a reason to load again:
  // Load more fetches its own page.
  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  // The pages belong to the search that was sent. While typing is still ahead
  // of the list, coming back runs what was typed, so from one page.
  const typedKey = `${tabKey}|${searchInput.trim()}|${codesKey}`;
  useRememberList('/bulletins', {
    search: searchInput,
    codes: codesInput,
    pages: typedKey === filterKey ? pages : 1
  });

  // Answers true when the page arrived and there is another after it.
  const loadPage = useCallback(async (page) => {
    const requestId = ++latestRequest.current;
    const requestKey = `${tabKey}|${query}|${codes}`;
    const tab = BULLETIN_TABS.find(entry => entry.key === tabKey);
    const params = new URLSearchParams(tab.params);
    if (query) params.set('q', query);
    if (codes) params.set('fault_codes', codes);
    params.set('page', String(page));
    params.set('limit', String(PAGE_SIZE));

    if (page === 1) setLoading(true); else setLoadingMore(true);
    setError('');
    try {
      const response = await authFetch(`/api/v1/bulletins?${params}`);
      const body = await response.json().catch(() => ({}));
      if (requestId !== latestRequest.current) return false;
      if (!response.ok) {
        setError(body.message || 'Could not load bulletins. Try again.');
        if (page === 1) {
          setRows([]);
          setPagination(null);
        }
        return false;
      }
      const data = Array.isArray(body.data) ? body.data : [];
      setRows(prev => {
        if (page === 1) return data;
        // A bulletin published between pages shifts the offsets; never show one twice.
        const seen = new Set(prev.map(row => row.bulletin_id));
        return [...prev, ...data.filter(row => !seen.has(row.bulletin_id))];
      });
      setPagination(body.pagination || null);
      return Boolean(body.pagination && body.pagination.page < body.pagination.totalPages);
    } catch (err) {
      if (requestId !== latestRequest.current) return false;
      setError('Could not load bulletins. Check your connection and try again.');
      return false;
    } finally {
      if (requestId === latestRequest.current) {
        setLoading(false);
        setLoadingMore(false);
        setLoadedKey(requestKey);
      }
    }
  }, [authFetch, tabKey, query, codes]);

  // A tab or search loads its first page. A list being returned to loads as
  // many pages as it had, one after another.
  useEffect(() => {
    let superseded = false;
    const upTo = pagesRef.current;
    (async () => {
      for (let page = 1; page <= upTo; page += 1) {
        // Stops at the last page, a failed load, or a newer tab or search.
        if (superseded || !(await loadPage(page))) return;
      }
    })();
    return () => { superseded = true; };
  }, [loadPage]);

  const loadMore = () => {
    const next = pagination.page + 1;
    setPages(next);
    loadPage(next);
  };

  const chooseTab = (key) => {
    setSearchParams(key === BULLETIN_TABS[0].key ? {} : { tab: key }, { replace: true });
  };

  const showLoading = loading || loadedKey !== filterKey;
  const filtering = Boolean(query || codes);
  const hasMore = Boolean(pagination && pagination.page < pagination.totalPages);

  return (
    <div className="bulletin-page">
      <div className="bulletin-header">
        <div>
          <h1>Bulletins</h1>
          <p>Fixes for repeated faults, and other know-how worth sharing with the engineers.</p>
        </div>
        <Link to="/bulletins/new" className="btn-primary bulletin-button">Write a bulletin</Link>
      </div>

      <div className="bulletin-tabs" role="tablist" aria-label="Which bulletins">
        {tabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === tabKey}
            className={`bulletin-tab${tab.key === tabKey ? ' active' : ''}`}
            onClick={() => chooseTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bulletin-filters">
        <div className="bulletin-filter">
          <label htmlFor="bulletin-search">Search</label>
          <input
            id="bulletin-search"
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Words in the title or text"
            autoComplete="off"
          />
        </div>
        <div className="bulletin-filter">
          <label htmlFor="bulletin-codes">Fault codes</label>
          <input
            id="bulletin-codes"
            type="text"
            value={codesInput}
            onChange={e => setCodesInput(e.target.value)}
            placeholder="e.g. E101, E205"
            autoComplete="off"
            autoCapitalize="characters"
          />
        </div>
      </div>

      <div role="tabpanel" aria-label={activeTab.label}>
        {error && <div className="bulletin-error" role="alert">{error}</div>}

        {showLoading ? (
          <div className="bulletin-loading">Loading…</div>
        ) : rows.length === 0 ? (
          !error && (
            <div className="bulletin-empty">
              {filtering ? 'No bulletins match that search.' : activeTab.empty}
            </div>
          )
        ) : (
          <>
            <ul className="bulletin-list">
              {rows.map(row => (
                <li key={row.bulletin_id}>
                  <BulletinCard bulletin={row} />
                </li>
              ))}
            </ul>
            <div className="bulletin-more">
              {pagination && pagination.total > rows.length && (
                <span className="bulletin-count">Showing {rows.length} of {pagination.total}</span>
              )}
              {hasMore && (
                <button
                  type="button"
                  className="btn-secondary bulletin-button"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Bulletins;
